import { ImisResponseItems, GenTableItem } from "../models/imis.models";
import { RestUtility } from "./rest.utility";

/**
 * Utilities for reading and managing iMIS generic table (gentable) lookup values.
 */
export class GenTablesUtility {

    private static readonly endpoint: string = "/api/gentable";

    /**
     * Retrieves the list of all gentable table names defined in iMIS via the custom `GetGenTableNames` execute operation.
     * @returns array of gentable table names, or null if the request failed
     */
    public static async getAllGenTables(): Promise<string[]> {
        let genTables: string[] = [];

        try {
            genTables = await RestUtility.restApiSendData<string[]>(`${this.endpoint}/_execute`, {
                "$type": "Asi.Soa.Core.DataContracts.GenericExecuteRequest, Asi.Contracts",
                "OperationName": "GetGenTableNames",
            });
        } catch (error) {
            genTables = null;
        }

        return genTables;
    }

    /**
     * Retrieves every record belonging to a specific gentable, paginating through all available results.
     * @param tableName name of the gentable to retrieve records for
     * @returns items collection containing all records for the table
     */
    public static getAllGenTableRecordsByTableName(tableName: string): Promise<ImisResponseItems<GenTableItem>> {
        return RestUtility.restApiGetDataAll<GenTableItem>(this.endpoint, { tableName });
    }

    /**
     * Fetches a single gentable record identified by its table name and code.
     * @param tableName name of the gentable
     * @param code code value identifying the record within the table
     * @returns the matching gentable record
     */
    public static getGenTableRecord(tableName: string, code: string): Promise<GenTableItem> {
        return RestUtility.restApiGetData<GenTableItem>(`${this.endpoint}/~${tableName}|${code}`);
    }

    /**
     * Creates a new gentable record.
     * @param recordData iMIS-format payload for the record to create
     * @returns the created record
     */
    public static addGenTableRecord(recordData: any): Promise<GenTableItem> {
        return RestUtility.restApiSendData(this.endpoint, recordData);
    }

    /**
     * Updates an existing gentable record identified by table name and code.
     * @param tableName name of the gentable
     * @param code code value identifying the record to update
     * @param recordData iMIS-format payload with updated values
     * @returns the updated record
     */
    public static updateGenTableRecord(tableName: string, code: string, recordData: any): Promise<GenTableItem> {
        return RestUtility.restApiSendData(`${this.endpoint}/~${tableName}|${code}`, recordData, "PUT");
    }
}